import { useEffect, useRef, useState } from "react";

import { useDebouncedCallback } from "@/hooks/use-debounced-callback";

/**
 * Local, debounced state for a facet bar's free-text input. Typing updates the
 * field immediately and reports upstream after 300ms; upstream changes (e.g.
 * back navigation) resync the field unless they echo our own write.
 */
export function useFacetSearchInput(
  search: string | null | undefined,
  onSearchChange: ((value: string | null) => void) | undefined,
) {
  const [value, setValue] = useState(search ?? "");
  const pendingSearchSync = useRef(false);

  useEffect(() => {
    if (pendingSearchSync.current) {
      pendingSearchSync.current = false;
      return;
    }
    setValue(search ?? "");
  }, [search]);

  const debouncedSearchChange = useDebouncedCallback((next: string) => {
    pendingSearchSync.current = true;
    onSearchChange?.(next || null);
  }, 300);

  return {
    value,
    onChange: (next: string) => {
      setValue(next);
      debouncedSearchChange(next);
    },
    onClear: () => {
      setValue("");
      pendingSearchSync.current = true;
      onSearchChange?.(null);
    },
  };
}
